import { RefObject, useEffect, useMemo, useState } from "react";
import { Wordopolis } from "@datagame/ui";
import classNames from "classnames/bind";

import { caseInsensitiveStringEqual } from "../../../utils/caseInsensitiveStringEqual";
import { COLOR_BRAND_ORANGE_BASE, COLOR_BRAND_ROSE_700 } from "../../../constants";
import { directionTransformers } from "../../../utils/directionTransformers";
import { getBoardPath } from "../../../utils/board/getBoardPath";
import { isWordPresent } from "../../../utils/isWordPresent";
import { useTime } from "../../../hooks/useTime";
import { useWordopolis } from "../../../hooks/useWordopolis";
import { WordopolisActionKind, WordopolisCellCords } from "../../../reducers/wordopolis";
import { WordopolisDifficulty } from "../../../gql/graphql";

import styles from "./WordopolisBoard.module.scss";

const cx = classNames.bind(styles);

const { Mark, Cell, Stage, Background } = Wordopolis;

export interface WordopolisBoardProps {
  parentRef: RefObject<HTMLDivElement>;
}

export const WordopolisBoard = ({ parentRef }: WordopolisBoardProps) => {
  const [
    {
      configuration: {
        board: flattenBoard,
        words: wordsPlacemnts,
        difficulty,
        numberOfColumns,
        numberOfRows,
        boardBackgroundColor,
        boardBackgroundImage,
        boardColor,
      },
      status: { isGameFinished, foundWords, toRate, isIntroFinished, showWords },
      stage: stageSize,
    },
    dispatch,
  ] = useWordopolis();
  const { getCurrentTime } = useTime();
  const { width, height } = stageSize;
  const words = wordsPlacemnts.map(({ value }) => value);
  const isBlurred = Boolean((!isGameFinished && toRate.length) || !isIntroFinished || showWords);

  const [notFoundWords, setNotFoundWords] = useState<WordopolisCellCords[][]>([]);
  const [activeCells, setActiveCells] = useState<WordopolisCellCords[]>([]);
  const [wrongWordCells, setWrongWordCells] = useState<WordopolisCellCords[]>([]);

  const board = useMemo(() => {
    const chunkSize = numberOfColumns;
    const array = [];
    for (let i = 0; i < flattenBoard.length; i += chunkSize) {
      const chunk = flattenBoard.slice(i, i + chunkSize);
      array.push(chunk);
    }
    return array;
  }, [flattenBoard]);

  const directions = useMemo(() => {
    if (difficulty === WordopolisDifficulty.Easy) {
      return ["s", "e"];
    }
    if (difficulty === WordopolisDifficulty.Medium) {
      return ["s", "e", "n", "w"];
    }
    return ["s", "e", "n", "w", "se", "sw", "ne", "nw"];
  }, [difficulty]);

  const getWordPath = (word: string) => {
    const wordPlacement = wordsPlacemnts.find(({ value }) => caseInsensitiveStringEqual(value, word));

    if (!wordPlacement) {
      return [];
    }

    const { startX, startY, direction } = wordPlacement;
    const transformer = directionTransformers[direction];

    return word.split("").reduce((acc: WordopolisCellCords[]) => {
      const lastCell = acc[acc.length - 1];

      if (!lastCell) {
        return [{ x: startX, y: startY }];
      }

      return [...acc, transformer(lastCell.x, lastCell.y)];
    }, []);
  };

  const foundWordsCords = useMemo(() => {
    const result: WordopolisCellCords[][] = [];

    if (foundWords.length === 0) {
      return result;
    }

    for (const word of foundWords) {
      result.push(getWordPath(word));
    }

    return result;
  }, [foundWords]);

  const markNewWord = (word: string) => {
    dispatch({
      type: WordopolisActionKind.INSERT_WORD,
      payload: { word, time: getCurrentTime() },
    });
    setActiveCells([]);
  };

  const handleCellClick = (row: number, cell: number) => {
    if (isGameFinished) {
      return;
    }

    if (activeCells.length === 0) {
      setActiveCells([{ x: cell, y: row }]);
      return;
    }

    const newActiveCell = { x: cell, y: row };
    const lastActiveCell = activeCells[0];

    // If the same cell is clicked, do nothing
    if (lastActiveCell.x === newActiveCell.x && lastActiveCell.y === newActiveCell.y) {
      return;
    }

    const path = getBoardPath(lastActiveCell, newActiveCell, directions, board);

    // If path can't be established, set clicked cell to starting cell
    if (!path) {
      return setActiveCells([newActiveCell]);
    }

    const word = path.map(({ x, y }) => board[y][x]).join("");

    // If word is already found, set clicked cell to starting cell
    if (isWordPresent(word, foundWords)) {
      return setActiveCells([newActiveCell]);
    }

    // If word is not in the list of words, reset active cells
    if (!isWordPresent(word, words)) {
      setWrongWordCells(path);
      return setActiveCells([]);
    }

    markNewWord(word);
  };

  const displayNotFoundWords = () => {
    const notFoundWordsPlacements = wordsPlacemnts.filter(({ value }) => !isWordPresent(value, foundWords));
    const cellsToHigliht: WordopolisCellCords[][] = [];

    for (const { value } of notFoundWordsPlacements) {
      cellsToHigliht.push(getWordPath(value));
    }

    setNotFoundWords(cellsToHigliht);
  };

  useEffect(() => {
    if (wrongWordCells.length > 0) {
      setTimeout(() => {
        setWrongWordCells([]);
      }, 800);
    }
  }, [wrongWordCells]);

  useEffect(() => {
    if (isGameFinished) {
      setActiveCells([]);
      displayNotFoundWords();
    }
  }, [isGameFinished]);

  const cellSize = Math.ceil(Math.min(width, height) / Math.min(numberOfColumns, numberOfRows));

  return (
    <Stage size={stageSize} parentRef={parentRef} className={cx({ blurred: isBlurred })}>
      <Background size={stageSize} color={boardBackgroundColor} backgroundImage={boardBackgroundImage || undefined} />
      {board.map((row, rowIndex) => {
        return row.map((cell: string, cellIndex: number) => (
          <Cell
            key={`${rowIndex}-${cellIndex}`}
            row={rowIndex}
            cell={cellIndex}
            size={cellSize}
            fontColor={boardColor}
            content={cell.toUpperCase()}
            onClick={() => handleCellClick(rowIndex, cellIndex)}
          />
        ));
      })}
      {foundWordsCords.map((wordCords, index) => (
        <Mark key={index} start={wordCords[0]} end={wordCords[wordCords.length - 1]} size={cellSize} />
      ))}
      {notFoundWords.map((wordCords, index) => (
        <Mark
          key={index}
          start={wordCords[0]}
          end={wordCords[wordCords.length - 1]}
          size={cellSize}
          color={COLOR_BRAND_ORANGE_BASE}
        />
      ))}
      {wrongWordCells.length > 0 && (
        <Mark
          start={wrongWordCells[0]}
          end={wrongWordCells[wrongWordCells.length - 1]}
          size={cellSize}
          color={COLOR_BRAND_ROSE_700}
        />
      )}
      {activeCells.length > 0 && (
        <Mark start={activeCells[0]} end={activeCells[activeCells.length - 1]} size={cellSize} />
      )}
    </Stage>
  );
};
